<template>
	<Loader v-if="loading" />
	<div v-else class="content">
		<div class="flex">
			<h1>Conductor jobs</h1>
			<div class="flex-grow text-right">
				<button v-on:click="load()">Refresh</button>
			</div>
		</div>

		<div class="margin-top flex align-items flex-wrap">
			<select v-model="filters.flow" v-on:change="applyFilters()">
				<option value="">[ All ]</option>
				<option :value="flow" v-for="flow in flows">{{ flow }}</option>
			</select>
			<select class="small-margin-left" v-model="filters.status" v-on:change="applyFilters()">
				<option value="">[ All ]</option>
				<option :value="status" v-for="status in statuses">{{ status }}</option>
			</select>
			<input
				class="small-margin-left"
				type="text"
				placeholder="Model type"
				v-model="filters.model_type"
				v-on:change="applyFilters()" />
			<input
				class="small-margin-left"
				type="number"
				placeholder="Model ID"
				v-model="filters.model_id"
				v-on:change="applyFilters()" />
			<input
				class="small-margin-left"
				type="date"
				title="Created after"
				v-model="filters.created_after"
				v-on:change="applyFilters()" />
			<input
				class="small-margin-left"
				type="date"
				title="Created before"
				v-model="filters.created_before"
				v-on:change="applyFilters()" />
		</div>

		<div class="small-margin-top">
			<button
				class="small small-margin-right"
				v-bind:class="{ gray: filters.status != status }"
				v-for="status in statuses"
				v-on:click="toggleStatus(status)">
				{{ status }}: {{ counts[status] }}
			</button>
			<span
				class="note"
				title="Pending or running jobs whose last attempt left an error: a step backing off through its retries, or one a catch already moved past.">
				hit an error recently: {{ counts.errored_recently }}
			</span>
		</div>

		<div class="margin-top">
			<table>
				<thead>
					<tr>
						<th>ID</th>
						<th>Flow</th>
						<th>Status</th>
						<th>Step</th>
						<th title="Reset to zero on every step transition">Attempts (this step)</th>
						<th>Model</th>
						<th>Run after</th>
						<th>Created</th>
						<th>Error</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="job in jobs.data" :key="job.id">
						<td>
							<router-link :to="'/conductor-job/' + job.id">{{ job.id }}</router-link>
						</td>
						<td>{{ job.flow }}</td>
						<td>
							<ConductorJobStatus :job="job" :now="now" />
						</td>
						<td><span class="note">{{ stepLabel(job) }}</span> {{ job.step }}</td>
						<td>{{ job.attempts }}</td>
						<td>
							<OrderLink v-if="job.model_type == 'order' && job.model_id" :id="job.model_id" />
							<template v-else-if="job.model_type">{{ job.model_type }} {{ job.model_id }}</template>
						</td>
						<td>{{ terminal(job) ? '' : formatDate(job.run_after) }}</td>
						<td>{{ formatDate(job.created_at) }}</td>
						<td :title="job.last_error">{{ truncate(job.last_error) }}</td>
					</tr>
				</tbody>
			</table>
		</div>

		<div class="small-margin-top flex align-items">
			<button
				class="small gray"
				:disabled="jobs.current_page <= 1"
				v-on:click="goToPage(jobs.current_page - 1)">Prev</button>
			<div class="small-margin-left small-margin-right">
				Page {{ jobs.current_page }} of {{ jobs.last_page }} ({{ jobs.total }} total)
			</div>
			<button
				class="small gray"
				:disabled="jobs.current_page >= jobs.last_page"
				v-on:click="goToPage(jobs.current_page + 1)">Next</button>
		</div>
	</div>
</template>

<script>
import Vue from 'vue'
import moment from 'moment'
import ConductorJobStatus from '@/components/ConductorJobStatus.vue'

export default {
  data: () => ({
    loading: false,
    jobs: {},
    counts: {},
    flows: [],
    now: '',
    page: 1,
    statuses: ['pending', 'running', 'done', 'failed', 'cancelled'],
    terminalStatuses: ['done', 'failed', 'cancelled'],
    filters: {
      flow: '',
      status: '',
      model_type: '',
      model_id: '',
      created_after: '',
      created_before: ''
    }
  }),
  watch: {
    '$route' () {
      this.readQuery()
      this.page = 1
      this.load()
    }
  },
  methods: {
    activeFilters () {
      var active = {}

      for (var key in this.filters) {
        if (this.filters[key] !== '' && this.filters[key] !== null) {
          active[key] = this.filters[key]
        }
      }

      return active
    },
    query () {
      var parts = ['page=' + this.page]
      const active = this.activeFilters()

      for (var key in active) {
        parts.push(key + '=' + encodeURIComponent(active[key]))
      }

      return parts.join('&')
    },
    readQuery () {
      for (var key in this.filters) {
        this.filters[key] = this.$route.query[key] || ''
      }
    },
    applyFilters () {
      // The route is the filter state: the watcher reloads on the change.
      this.$router.replace({ query: this.activeFilters() }).catch(() => {})
    },
    toggleStatus (status) {
      this.filters.status = this.filters.status == status ? '' : status
      this.applyFilters()
    },
    goToPage (page) {
      this.page = page
      this.load()
    },
    terminal (job) {
      return this.terminalStatuses.indexOf(job.status) >= 0
    },
    stepLabel (job) {
      if (job.status == 'pending') {
        return 'next'
      }

      if (job.status == 'running') {
        return 'at'
      }

      return job.status == 'done' ? 'finished at' : 'stopped at'
    },
    formatDate (value) {
      return value ? moment.utc(value).local().format('YYYY-MM-DD HH:mm') : ''
    },
    truncate (value) {
      if (!value) {
        return ''
      }

      return value.length > 120 ? value.slice(0, 120) + '...' : value
    },
    load () {
      Vue.$api.get('/conductor-jobs?' + this.query(), {}, {
        before: () => this.loading = true,
        always: () => this.loading = false,
        callback: data => this.parse(data)
      })
    },
    parse (data) {
      this.jobs = data.jobs
      this.counts = data.counts
      this.flows = data.flows
      this.now = data.now
    }
  },
  components: {
    ConductorJobStatus
  },
  created () {
    this.readQuery()
    this.load()
  }
}
</script>
